import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../dates.ts';
import { Refusal } from './PropertiesPanel.tsx';
import { ReasonFields, useReasonReady } from './VisitDetails.tsx';
import { useChecksNotDeposited, useFurtherMarshalling, useSetFurtherMarshalling, type MarshalOrder } from '../api/marshalling.ts';

/**
 * Whether a court order authorizes FURTHER marshalling (166, A-#354), in the Legal section: Legal's to record
 * (may_record_commission(): Legal at write in Legal, tiers 1-2), as a whole version of the order through 128's edit,
 * with a reason. Yes opens one Finance task, "Marshal further funds under the order of <date>", closed by the check,
 * the move or the statements recorded under the order. Nothing is ever read from the order's text, and nothing
 * follows from an amount left at a bank.
 */

const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

const orderWords = (o: { order_type: string | null; signed_date: string | null }) =>
  `${o.order_type ?? 'Order'}${o.signed_date ? ` of ${formatDate(o.signed_date)}` : ', date not known'}`;

export function FurtherMarshallingPanel({ clientId }: { clientId: string }) {
  const q = useFurtherMarshalling(clientId);
  const [said, setSaid] = useState<string | null>(null);
  if (q.error && !q.data) return <div className="px-4 pb-3"><Refusal error={q.error} /></div>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading…</p>;
  const { orders, institutions, may_set_order } = q.data;
  const partial = institutions.filter((i) => i.partial);
  if (partial.length === 0 && !orders.some((o) => o.authorizes_further_marshalling)) {
    return <p className="px-4 pb-3 text-neutral-600">No bank was partly marshalled under an order, so no order is needed to marshal more.</p>;
  }
  return (
    <div>
      {said && <p role="status" className="px-4 pt-2 text-sm text-neutral-800">{said}</p>}
      {partial.length > 0 && (
        <p className="px-4 pt-2 text-sm text-neutral-700">
          Partly marshalled up to an order: {partial.map((i) => i.name).join(', ')}. Only a later order that allows more opens new work.
        </p>
      )}
      <ul className="mt-2 divide-y divide-neutral-100 border-t border-neutral-200">
        {orders.map((o) => (
          <li key={o.id} className="px-4 py-3">
            <OrderLine clientId={clientId} order={o} banks={institutions} may={may_set_order} onSaid={setSaid} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrderLine({ clientId, order, banks, may, onSaid }: {
  clientId: string; order: MarshalOrder; banks: { id: string; name: string }[]; may: boolean; onSaid: (x: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [yes, setYes] = useState(order.authorizes_further_marshalling);
  const [named, setNamed] = useState<string[]>(order.further_institution_ids);
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const ready = useReasonReady(reason, text);
  const set = useSetFurtherMarshalling(clientId);
  const names = banks.filter((b) => order.further_institution_ids.includes(b.id)).map((b) => b.name);
  const id = `fm-${order.id}`;
  return (
    <>
      <p className="font-semibold text-neutral-900">{orderWords(order)}</p>
      <p className="text-sm text-neutral-700">
        {order.authorizes_further_marshalling
          ? `Authorizes further marshalling${names.length ? `: ${names.join(', ')}` : ' at any of the client\'s banks'}.`
          : 'Does not authorize further marshalling.'}
      </p>
      {may && !open && <button type="button" className={`${secondary} mt-2`} onClick={() => setOpen(true)}>Record whether it authorizes further marshalling</button>}
      {open && (
        <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3" onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          set.mutate({ orderId: order.id, authorizes: yes, institution_ids: yes ? named : [], reason, ...(text.trim() ? { reason_text: text.trim() } : {}) }, {
            onSuccess: () => { setOpen(false); onSaid(yes ? 'Recorded. A Finance task opens to marshal the further funds under this order.' : 'Recorded: the order does not authorize further marshalling. An open task stays with its holder to close.'); },
          });
        }}>
          <fieldset>
            <legend className={label}>This order</legend>
            <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
              <input type="radio" name={`${id}-yes`} className="mt-1 size-5 shrink-0" checked={yes} onChange={() => setYes(true)} />
              <span className="text-neutral-900">Authorizes further marshalling</span>
            </label>
            <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
              <input type="radio" name={`${id}-yes`} className="mt-1 size-5 shrink-0" checked={!yes} onChange={() => setYes(false)} />
              <span className="text-neutral-900">Does not</span>
            </label>
          </fieldset>
          {yes && banks.length > 0 && (
            <fieldset>
              <legend className={label}>The banks it reaches <span className="font-normal text-neutral-500">— optional; none named means any of the client&rsquo;s</span></legend>
              {banks.map((b) => (
                <label key={b.id} className="flex min-h-[44px] cursor-pointer items-start gap-3">
                  <input type="checkbox" className="mt-1 size-5 shrink-0" checked={named.includes(b.id)}
                         onChange={(e) => setNamed((x) => (e.target.checked ? [...x, b.id] : x.filter((y) => y !== b.id)))} />
                  <span className="text-neutral-900">{b.name}</span>
                </label>
              ))}
            </fieldset>
          )}
          <p className={hint}>Recorded on the order as a new version, with the reason. Nothing is read from the order&rsquo;s text.</p>
          <ReasonFields reason={reason} text={text} setReason={setReason} setText={setText} idPrefix={id} />
          {set.error && <Refusal error={set.error} />}
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={primary} disabled={!ready || set.isPending}>Record</button>
            <button type="button" className={secondary} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}
    </>
  );
}

/**
 * "Checks in hand, not deposited" (166, P-33 R3 7): triage's Finance view. Each check with no deposit recorded from its
 * 10th business day (weekends skipped, no holidays). A list, never a notification or a task. No amount is shown here.
 */
export function ChecksNotDeposited() {
  const q = useChecksNotDeposited(true);
  if (q.error && !q.data) return <Refusal error={q.error} />;
  if (!q.data) return <p className="text-neutral-600">Loading the checks…</p>;
  const rows = q.data.checks;
  return (
    <section aria-labelledby="checks-not-deposited" className="mt-5">
      <h2 id="checks-not-deposited" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">
        Checks in hand, not deposited ({rows.length})
      </h2>
      <p className={hint}>A check with no deposit recorded from its 10th business day. Record the deposit on the client&rsquo;s Finance section.</p>
      {rows.length === 0 ? (
        <p className="mt-2 text-neutral-600">None.</p>
      ) : (
        <ul className="mt-2 divide-y divide-neutral-100 rounded-lg border border-neutral-200">
          {rows.map((r) => (
            <li key={r.check_action_id} className="flex flex-wrap items-baseline gap-3 px-3 py-2">
              <Link to={`/clients/${r.client_id}`} className="link inline-flex min-h-[44px] items-center font-semibold">{r.client_ref ?? 'The client'}</Link>
              <span className="text-neutral-800">{r.institution}</span>
              <span className="ml-auto text-sm text-neutral-700 tabular-nums">received {formatDate(r.received_on)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
